import { Link } from "react-router-dom";
import { ArrowLeft, PlayCircle, Heart, Users, Sparkles, ShieldCheck, Wallet, Feather, Compass, LifeBuoy, BookOpen } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

const RESOURCE_AREAS = [
  {
    icon: Heart,
    title: "Marriage & Relationships",
    body: "Practical, everyday guidance for strengthening the relationships that matter most — with content for couples in any season.",
  },
  {
    icon: Users,
    title: "Parenting & Family",
    body: "Honest support for the day-to-day realities of parenting, family dynamics, and life at every age and stage.",
  },
  {
    icon: Sparkles,
    title: "Emotional Well-Being",
    body: "Short, digestible material on anxiety, mood, and finding steadiness through hard seasons.",
  },
  {
    icon: ShieldCheck,
    title: "Stress & Resilience",
    body: "Practical ways to steady yourself when work and life feel heavy — and to build capacity for the next hard day.",
  },
  {
    icon: Wallet,
    title: "Financial Well-Being",
    body: "Approachable guidance on budgeting, saving, debt, and building healthy financial habits — free of jargon.",
  },
  {
    icon: Feather,
    title: "Grief & Loss",
    body: "Quiet, honest material for walking through loss — with room for the long, uneven road grief actually takes.",
  },
  {
    icon: Compass,
    title: "Personal Growth",
    body: "Encouragement for developing purpose, habits, and character — with room to reflect and take small next steps.",
  },
  {
    icon: LifeBuoy,
    title: "Recovery & Life Challenges",
    body: "Support for harder chapters — setbacks, unhealthy patterns, and starting again — offered with dignity and without shame.",
  },
  {
    icon: BookOpen,
    title: "Family-Friendly & Children's Content",
    body: "Curated selections your children can enjoy too, so the whole family can benefit from the library together.",
  },
];

/**
 * CCCHAPS On Demand — employee-facing landing page at /care-portal/on-demand.
 * Explains the digital video/resource library available to eligible employees
 * and their families. Access button is intentionally a placeholder — the real
 * URL will be wired in by the admin separately.
 * Wrapped by the site Layout via App.js so the Care Portal chatbox and nav are present.
 */
export default function OnDemandPage() {
  usePageMeta({
    title: "CCCHAPS On Demand | Care Portal",
    description:
      "Eligible employees and their families can access a digital library of practical video resources on marriage, parenting, well-being, finances, and personal growth.",
    noindex: true,
  });

  return (
    <div data-testid="on-demand-page" className="bg-white">
      <section
        data-testid="on-demand-hero"
        className="relative py-16 md:py-24"
        aria-labelledby="on-demand-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/care-portal"
            data-testid="on-demand-back"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#4A6FA5] mb-6"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to Care Portal
          </Link>

          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">CCCHAPS On Demand</p>
            <h1
              id="on-demand-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mt-3 text-balance"
            >
              Support for Life, Work, and Family — Available Anytime
            </h1>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Life outside of work shapes how we show up inside of it. CCCHAPS On Demand gives eligible employees and their families a
              digital library of practical, everyday resources designed to encourage and equip you at your own pace, in your own time.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              Whether you have a spare few minutes or a quiet evening at home, you can explore short videos and articles built for real
              life — no clinical jargon, no clocking in, no waiting rooms.
            </p>
          </div>

          {/* Warm human photograph */}
          <figure
            data-testid="on-demand-hero-image"
            className="mt-10 rounded-2xl overflow-hidden border border-slate-200 shadow-sm"
          >
            <div className="aspect-[16/9] w-full bg-slate-100">
              <img
                src="/images/care-portal-on-demand/on-demand.jpg"
                alt="Person comfortably exploring digital resources on a laptop at home"
                loading="lazy"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 40%" }}
              />
            </div>
          </figure>

          {/* Access CTA */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <a
              href="#"
              aria-disabled="true"
              onClick={(e) => e.preventDefault()}
              data-testid="on-demand-access-btn"
              title="Access link coming soon"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-[#4A6FA5] text-white text-sm sm:text-base font-semibold shadow-sm cursor-not-allowed opacity-70"
            >
              <PlayCircle className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
              Access CCCHAPS On Demand
            </a>
            <p className="text-xs text-slate-500 max-w-xs" data-testid="on-demand-access-note">
              Access instructions will be provided by your organization. This button will become active once your access link is set up.
            </p>
          </div>

          {/* Resource areas */}
          <div className="mt-16">
            <h2 className="font-heading text-2xl text-slate-900">What you'll find inside</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">
              The library is organized around the parts of life employees ask about most. Explore any area — or start wherever you feel drawn.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {RESOURCE_AREAS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  data-testid={`on-demand-area-${title.split(/\s|&/)[0].toLowerCase()}`}
                  className="rounded-2xl border border-slate-200 bg-white p-6"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-3">
                    <Icon className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" />
                  </div>
                  <p className="font-heading text-lg text-slate-900">{title}</p>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Who it's for */}
          <div className="mt-16 rounded-2xl bg-white border border-slate-200 p-6 md:p-8">
            <h3 className="font-heading text-2xl text-slate-900">Who it's for</h3>
            <p className="mt-3 text-slate-700 leading-relaxed">
              CCCHAPS On Demand is available to eligible employees of enrolled organizations and to their immediate family members at home.
              Content is thoughtfully curated so children and teens can enjoy age-appropriate selections alongside adults — it's designed to
              benefit the whole household, not just the person at work.
            </p>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">
              Use of the library is entirely voluntary. Nothing you view is reported back to your employer.
            </p>
          </div>

          {/* Closing */}
          <div className="mt-14 text-center max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl sm:text-3xl text-slate-900">A quiet resource, ready when you are.</h3>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Some seasons of life call for a conversation with a chaplain. Others just call for a few honest minutes to yourself.
              CCCHAPS On Demand is there for the second kind — a steady library you can return to whenever it's helpful.
            </p>
            <p className="mt-6 font-heading text-xl text-[#4A6FA5]">Encouragement for everyday life.</p>
            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/care-portal/request-support"
                data-testid="on-demand-request-support-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border-2 border-[#4A6FA5] text-[#4A6FA5] hover:bg-[#F0F4F8] text-sm sm:text-base font-semibold transition"
              >
                Talk with a chaplain instead
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
